/**
 * Context React - DependencyContext
 *
 * Fournit l'accès aux dépendances de l'application à tous les composants
 * via le DependencyContainer
 */

import { createContext, useContext, ReactNode } from 'react';
import { DependencyContainer } from '../../infrastructure/config/DependencyContainer';
import { ITransactionRepository } from '../../application/ports/output/ITransactionRepository';
import { IAccountRepository } from '../../application/ports/output/IAccountRepository';
import { ICashRegisterRepository } from '../../application/ports/output/ICashRegisterRepository';
import { IBiometricService } from '../../application/ports/output/IBiometricService';
import { ICardReaderService } from '../../application/ports/output/ICardReaderService';
import { IAuditLogger } from '../../application/ports/output/IAuditLogger';
import { IDepositGateway } from '../../application/ports/output/IDepositGateway';
import { ICbsAccountLookup } from '../../application/ports/output/ICbsAccountLookup';
import { ITypePieceService } from '../../application/ports/output/ITypePieceService';
import { CardLifeWithdrawalGateway } from '../../infrastructure/adapters/CardLifeWithdrawalGateway';

interface DependencyContextValue {
  transactionRepository: ITransactionRepository;
  accountRepository: IAccountRepository;
  cashRegisterRepository: ICashRegisterRepository;
  biometricService: IBiometricService;
  cardReaderService: ICardReaderService;
  auditLogger: IAuditLogger;
  depositGateway: IDepositGateway | null;
  withdrawalGateway: CardLifeWithdrawalGateway | null;
  cbsAccountLookup: ICbsAccountLookup;
  typePieceService: ITypePieceService;
}

const DependencyContext = createContext<DependencyContextValue | null>(null);

interface DependencyProviderProps {
  children: ReactNode;
}

/**
 * Provider qui initialise et fournit toutes les dépendances
 */
export function DependencyProvider({ children }: DependencyProviderProps) {
  const container = DependencyContainer.getInstance();

  const value: DependencyContextValue = {
    transactionRepository: container.transactionRepository,
    accountRepository: container.accountRepository,
    cashRegisterRepository: container.cashRegisterRepository,
    biometricService: container.biometricService,
    cardReaderService: container.cardReaderService,
    auditLogger: container.auditLogger,
    depositGateway: container.depositGateway,
    withdrawalGateway: container.withdrawalGateway,
    cbsAccountLookup: container.cbsAccountLookup,
    typePieceService: container.typePieceService,
  };

  return (
    <DependencyContext.Provider value={value}>
      {children}
    </DependencyContext.Provider>
  );
}

/**
 * Hook pour accéder aux dépendances
 */
export function useDependencies(): DependencyContextValue {
  const context = useContext(DependencyContext);
  if (!context) {
    throw new Error('useDependencies must be used within a DependencyProvider');
  }
  return context;
}
